import React, { FunctionComponent, useEffect } from 'react';

import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';
import Loader from 'common/components/Loader/Loader';

import { Subject, Country, School, Blog } from 'app/types';

import {
  useLazyGetSubjectsQuery,
  useLazyGetCountriesQuery,
  useLazyGetSchoolsQuery,
  useLazyGetPartnersQuery,
  useLazyGetBlogEntriesQuery,
  useGetHomePageLandingQuery,
} from 'services/exploreApi';
import Text from 'common/components/Text/Text';
import DestinationCard from 'common/components/DestinationCard/DestinationCard';
import UniversityCard from 'common/components/UniversityCard/UniversityCard';
import VariableLanding from 'features/landings/components/VariableLanding';

import { dynamicScripts, navigateTop } from 'common/utils';
import {
  TitleImg,
  Info1,
  Info2,
  Info3,
  Book,
  Snap,
  Cap,
  World,
  Newsletter,
} from 'assets/landing/index';

import styles from './Landing.module.scss';
import textStyles from 'common/styles/text.module.scss';

const Landing: FunctionComponent = () => {
  const { data: landing, isLoading } = useGetHomePageLandingQuery();
  const isLanding = location.pathname == '/';
  const [getSubjects, { data: subjects }] = useLazyGetSubjectsQuery();
  const [getCountries, { data: countries }] = useLazyGetCountriesQuery();
  const [getSchools, { data: schools }] = useLazyGetSchoolsQuery();
  const [getPartners, { data: partners }] = useLazyGetPartnersQuery();
  const [getBlogs, { data: blogEntries }] = useLazyGetBlogEntriesQuery();
  useEffect(() => {
    document.title = 'Unify: study business in Europe and UK, apply in only one place.';
  }, [isLanding]);

  useEffect(() => {
    if (!isLoading && !landing) {
      getSubjects('');
      getCountries('');
      getSchools({});
      getPartners();
      getBlogs();
    }
  }, [isLoading]);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    document.title = 'Unify: study business in Europe and UK, apply in only one place.';
  }, [isLanding]);
  if (isLoading) {
    return <Loader />;
  }
  if (landing !== undefined && landing !== null && isLoading === false) {
    return <VariableLanding landing={landing} />;
  }

  return (
    <div className={styles.landingContainer}>
      <div>
        <div className={styles.firstContainer}>
          <div className={styles.flexColContainer}>
            <div className={`${styles.txtTitleContainer}`}>
              <div className={styles.titleContainer}>
                {isMobile ? (
                  <div>
                    <h1 className={textStyles['text-primary-35-600']}>
                      <span className={styles.titleFirstLine}>The smart way to</span> <br />
                      <span className={styles.titleSecondLine}>
                        apply to business school abroad.
                      </span>
                    </h1>
                  </div>
                ) : (
                  <div>
                    <h1 className={textStyles['text-primary-50-600']}>
                      <span className={styles.titleFirstLine}>The smart way to apply to</span>{' '}
                      <br />
                      <span className={styles.titleSecondLine}>business school </span>
                      <span className={styles.titleAbroad}>abroad.</span>
                    </h1>
                  </div>
                )}
                <div className={styles.paragraph}>
                  <p
                    className={`pt-4
                      ${
                        isMobile
                          ? textStyles['text-secondary-14-400']
                          : textStyles['text-secondary-20-400']
                      }
                    `}
                  >
                    With Unify you can explore, match and apply to 200+ universities all around the
                    world to study master&#39;s degree in business. Get expert guidance at each step
                    you take {!isMobile && '— all at zero cost.'}
                  </p>
                </div>
                <div className={styles.rowTitle}>
                  <p
                    className={styles.descriptionText}
                    style={{ fontWeight: 400, fontSize: 14, paddingTop: '20px' }}
                  >
                    —{' '}
                  </p>
                  <p
                    className={styles.primaryText}
                    style={{ fontWeight: 600, fontSize: 20, paddingTop: '20px' }}
                  >
                    {' '}
                    all at zero cost.
                  </p>
                </div>
                <div className={styles.btnStartContainer}>
                  <Link to="explore/programs" onClick={() => navigateTop()}>
                    <div className={`btn btn-primary ${styles.startBtn}`}>Explore Programs</div>
                  </Link>
                </div>
              </div>
              {/* <div className={styles.myAppContainer}>
                <u className={styles.descriptionText}>
                  <p style={{ fontSize: 14, paddingTop: '20px' }}>Go to my application</p>
                </u>
              </div> */}
            </div>
            {!isMobile && (
              <div className={styles.indicatorsContainer}>
                <div className={`${styles.indicDPContainer}`}>
                  <div className="col-4 d-flex flex-column align-items-center">
                    <p className={`mb-1 ${styles.indicator}`}>100%</p>
                    <p
                      className={`mt-0 mb-0 ${textStyles['text-secondary-18-500']}`}
                      style={{ width: 'max-content' }}
                    >
                      Success Rate
                    </p>
                  </div>
                  <div className="col-4 d-flex flex-column align-items-center">
                    <p className={`mb-1 ${styles.indicator}`}>250+</p>
                    <p className={`mt-0 mb-0 ${textStyles['text-secondary-18-500']}`}>
                      Universities
                    </p>
                  </div>
                  <div className="col-4 d-flex flex-column align-items-center">
                    <p className={`mb-1 ${styles.indicator}`}>2000+</p>
                    <p className={`mt-0 mb-0 ${textStyles['text-secondary-18-500']}`}>Programs</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={`${styles.titleImgContainer}`}>
            <img src={TitleImg} className={styles.titleImage} alt="TitleImg" />
          </div>
          {isMobile && (
            <div className={styles.indicatorsContainer}>
              <div className="d-flex flex-row justify-content-around pb-4">
                <div className="col-4 d-flex flex-column align-items-center">
                  <p className={`mb-1 ${styles.indicator}`}>100%</p>
                  <p className={`mt-0 mb-0 ${textStyles['text-tertiary-12-500']}`}>Success Rate</p>
                </div>
                <div className="col-4 d-flex flex-column align-items-center">
                  <p className={`mb-1 ${styles.indicator}`}>140+</p>
                  <p className={`mt-0 mb-0 ${textStyles['text-tertiary-12-500']}`}>Universities</p>
                </div>
                <div className="col-4 d-flex flex-column align-items-center">
                  <p className={`mb-1 ${styles.indicator}`}>2000+</p>
                  <p className={`mt-0 mb-0 ${textStyles['text-tertiary-12-500']}`}>Programs</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.colContainer}>
          <div>
            <p
              className={
                isMobile ? textStyles['text-primary-25-700'] : textStyles['text-primary-36-700']
              }
            >
              Unify helps you succeed
            </p>
            <p className={styles.bar}></p>
          </div>
          <div>
            <div className={`${styles.infoCard}`}>
              <div className={`${!isMobile && 'd-flex flex-row justify-content-around'}`}>
                <div
                  className={
                    isMobile
                      ? `row ${styles.infoRow}`
                      : 'col-10 d-flex flex-column align-items-center'
                  }
                >
                  <div className={isMobile ? 'col-2' : ''}>
                    <img src={Book} className={styles.infoImage} alt="book" />
                  </div>
                  <div className={`${isMobile ? 'col-10 ps-4' : 'mt-4 text-center'}`}>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-primary-18-600']
                          : textStyles['text-primary-28-600']
                      }
                    >
                      Find your dream school
                    </p>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-secondary-12-400']
                          : textStyles['text-secondary-16-400']
                      }
                    >
                      Our recommendation algorithm and complete list of schools help you find your
                      dream school.
                    </p>
                  </div>
                </div>
              </div>
              <div className={`${!isMobile && 'd-flex flex-row justify-content-around'}`}>
                <div
                  className={
                    isMobile
                      ? `row ${styles.infoRow}`
                      : 'col-10 d-flex flex-column align-items-center'
                  }
                >
                  <div className={isMobile ? 'col-2' : ''}>
                    <img src={Cap} className={styles.infoImage} alt="cap" />
                  </div>
                  <div className={`${isMobile ? 'col-10 ps-4' : 'mt-4 text-center'}`}>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-primary-18-600']
                          : textStyles['text-primary-28-600']
                      }
                    >
                      Maximise your chances
                    </p>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-secondary-12-400']
                          : textStyles['text-secondary-16-400']
                      }
                    >
                      Matching capabilities and personalized help increase your chances of studying
                      abroad.
                    </p>
                  </div>
                </div>
              </div>
              <div className={`${!isMobile && 'd-flex flex-row justify-content-around'}`}>
                <div
                  className={
                    isMobile
                      ? `row ${styles.infoRow}`
                      : 'col-10 d-flex flex-column align-items-center'
                  }
                >
                  <div className={isMobile ? 'col-2' : ''}>
                    <img src={Snap} className={styles.infoImage} alt="snap" />
                  </div>
                  <div className={`${isMobile ? 'col-10 ps-4' : 'mt-4 text-center'}`}>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-primary-18-600']
                          : textStyles['text-primary-28-600']
                      }
                    >
                      Expert help for you
                    </p>
                    <p
                      className={
                        isMobile
                          ? textStyles['text-secondary-12-400']
                          : textStyles['text-secondary-16-400']
                      }
                    >
                      From choosing schools to submitting applications and visa procedures, our
                      experts are happy to help.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.infoTitle}>
            <p
              className={
                isMobile ? textStyles['text-primary-25-700'] : textStyles['text-primary-36-700']
              }
            >
              Do It the Unify Way!
            </p>
            <p className={styles.bar}></p>
          </div>
          <div className={`${!isMobile && 'd-flex flex-row mt-5 pt-5'}`}>
            <div className={isMobile ? '' : 'col-6 d-flex flex-column'}>
              <div className={styles.infoTextContainer}>
                <p
                  className={
                    isMobile ? textStyles['text-secondary-18-600'] : textStyles['text-blue-20-700']
                  }
                >
                  Explore
                </p>
                {isMobile ? (
                  <p
                    className={textStyles['text-primary-19-600']}
                    style={{ maxWidth: '70%', textAlign: 'center' }}
                  >
                    2000+ business school programs in one place
                  </p>
                ) : (
                  <p className={textStyles['text-primary-36-700']}>
                    2000+ business school programs in one place
                  </p>
                )}
                <div className={`${isMobile && 'd-none'}`}>
                  <p className={textStyles['text-secondary-16-400']}>
                    We bring together a complete database of programs taught by European, UK, and US
                    business schools in one place, choose according to your preference and profile.
                  </p>
                </div>
              </div>
              {!isMobile && (
                <div className={styles.btnContainer}>
                  <Link to="explore/programs" onClick={() => navigateTop()}>
                    <div className={`btn btn-primary ${styles.infoBtn}`}>Discover programs</div>
                  </Link>
                </div>
              )}
            </div>
            <div
              className={
                isMobile
                  ? 'd-flex flex-row justify-content-center'
                  : 'col-6 d-flex flex-column align-items-center'
              }
            >
              <img src={Info1} className={styles.infoImageContainer} alt="Info1" />
            </div>
            {isMobile && (
              <div className={styles.btnContainer}>
                <Link to="explore/programs" onClick={() => navigateTop()}>
                  <div className={`btn btn-primary ${styles.infoBtn}`}>Discover programs</div>
                </Link>
              </div>
            )}
          </div>
          <div className={`${!isMobile && 'd-flex flex-row-reverse mt-5 pt-5'}`}>
            <div className={isMobile ? '' : 'col-6 d-flex flex-column'}>
              <div className={styles.infoTextContainer}>
                <p
                  className={
                    isMobile ? textStyles['text-secondary-18-600'] : textStyles['text-blue-20-700']
                  }
                >
                  Choose
                </p>
                {isMobile ? (
                  <p
                    className={textStyles['text-primary-19-600']}
                    style={{ maxWidth: '90%', textAlign: 'center' }}
                  >
                    Personalised recommendations that match your profile
                  </p>
                ) : (
                  <p className={textStyles['text-primary-36-700']}>
                    Personalised recommendations that match your profile
                  </p>
                )}
                <div className={`${isMobile && 'd-none'}`}>
                  <p className={textStyles['text-secondary-16-400']}>
                    At Unify Apply, we examine your profile and preference and generate a tailored
                    list of programs and schools that would be the best match for you.
                  </p>
                </div>
              </div>
              {!isMobile && (
                <div className={styles.btnContainer}>
                  <a href="/profile" target="_blank" rel="noopener noreferrer">
                    <div className={`btn btn-primary ${styles.infoBtn}`}>Get Recommendation</div>
                  </a>
                </div>
              )}
            </div>
            <div
              className={
                isMobile
                  ? 'd-flex flex-row justify-content-center'
                  : 'col-6 d-flex flex-column align-items-center'
              }
            >
              <img src={Info2} className={styles.infoImageContainer} alt="Info2" />
            </div>
            {isMobile && (
              <div className={styles.btnContainer}>
                <a href="/profile" target="_blank" rel="noopener noreferrer">
                  <div className={`btn btn-primary ${styles.infoBtn}`}>Get Recommendation</div>
                </a>
              </div>
            )}
          </div>

          <div className={`${!isMobile && 'd-flex flex-row mt-5 pt-5'}`}>
            <div className={isMobile ? '' : 'col-6 d-flex flex-column'}>
              <div className={styles.infoTextContainer}>
                <p
                  className={
                    isMobile ? textStyles['text-secondary-18-600'] : textStyles['text-blue-20-700']
                  }
                >
                  Apply
                </p>
                {isMobile ? (
                  <p
                    className={textStyles['text-primary-19-600']}
                    style={{ maxWidth: '60%', textAlign: 'center' }}
                  >
                    Seamless application in only one place
                  </p>
                ) : (
                  <p className={textStyles['text-primary-36-700']}>
                    Seamless application in only one place
                  </p>
                )}
                <div className={`${isMobile && 'd-none'}`}>
                  <p className={textStyles['text-secondary-16-400']}>
                    Our platform enables students to apply to multiple programs simultaneously. This
                    saves them valuable time and energy, your application process has never been so
                    simple.
                  </p>
                </div>
              </div>
              {!isMobile && (
                <div className={styles.btnContainer}>
                  <div
                    className={`btn btn-primary ${styles.infoBtn}`}
                    onClick={() => window.open(`/login`, '_blank')}
                  >
                    Start an Application
                  </div>
                </div>
              )}
            </div>
            <div
              className={
                isMobile
                  ? 'd-flex flex-row justify-content-center'
                  : 'col-6 d-flex flex-column align-items-center'
              }
            >
              <img src={Info3} className={styles.infoImageContainer} alt="Info3" />
            </div>
            {isMobile && (
              <div className={styles.btnContainer}>
                <div
                  className={`btn btn-primary ${styles.infoBtn}`}
                  onClick={() =>
                    window.open(`https://blog.unifyapply.com/free-application-review/`, '_blank')
                  }
                >
                  Start an Application
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${styles.scheduleContainer}`}>
          <div className={`${isMobile ? 'd-flex flex-column' : 'd-flex row-direction'}`}>
            <div className={`${isMobile ? 'd-flex flex-row' : 'col-6'}`}>
              <div className={`${isMobile ? 'd-flex flex-column' : ''}`}>
                <p
                  className={
                    isMobile
                      ? textStyles['text-secondary-14-400']
                      : textStyles['text-secondary-16-400']
                  }
                >
                  Want to know more?
                </p>
                <p
                  className={
                    isMobile ? textStyles['text-primary-20-600'] : textStyles['text-primary-45-600']
                  }
                >
                  {isMobile ? (
                    <span className={styles.textContainer}>
                      Schedule a free consulting Session with Our Unify Expert
                    </span>
                  ) : (
                    'Schedule a free consulting Session with Our Unify Expert'
                  )}
                </p>
              </div>
            </div>
            <div
              className={`${
                isMobile
                  ? 'd-flex flex-row justify-content-end'
                  : 'col-5 d-flex flex-column justify-content-center'
              }`}
            >
              <div
                className={`${
                  isMobile
                    ? `d-flex justify-content-end ${styles.bookBtnMobile}`
                    : 'd-flex justify-content-end'
                }`}
              >
                <a
                  href="https://unifyapply.com/experts/request-meeting/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={`btn btn-primary ${styles.bookBtn}`}>
                    {isMobile ? 'Book' : 'Book a Meeting'}
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className={`${styles.discoverContainer}`}>
          <div
            className={`${
              !isMobile && styles.discoverTitle
            } d-flex flex-row justify-content-between mt-5`}
          >
            <p
              className={`${
                isMobile ? textStyles['text-primary-18-600'] : textStyles['text-primary-24-600']
              } col-10`}
            >
              Discover popular subjects
            </p>
            {isMobile ? (
              <Link
                className={styles.viewMoreMobile}
                to="explore/programs"
                onClick={() => navigateTop()}
              >
                <u className="col-2">more</u>
              </Link>
            ) : (
              <Link className={styles.viewMore} to="explore/programs" onClick={() => navigateTop()}>
                <p>View more &nbsp;&nbsp;&rarr;</p>
              </Link>
            )}
          </div>
          <p
            className={`${
              isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-24-400']
            }`}
          >
            Identify the right program and business schools for you. Search by subject to find the
            perfect fit for you.
          </p>
          <div className={styles.tagContainer}>
            {subjects?.data.slice(0, 10).map((subject: Subject) => (
              <Link
                to={`/explore/programs?subject_id=${subject.id}`}
                key={subject.id}
                onClick={() => navigateTop()}
              >
                <div className={styles.tag}>
                  <div className={styles.text}>{subject.name}</div>
                </div>
              </Link>
            ))}
          </div>
          <div
            className={`${
              !isMobile && styles.discoverTitle
            } d-flex flex-row justify-content-between mt-5`}
          >
            <p
              className={`${
                isMobile ? textStyles['text-primary-18-600'] : textStyles['text-primary-24-600']
              } col-10`}
            >
              Discover popular destinations
            </p>
          </div>
          <p
            className={`${
              isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-24-400']
            }`}
          >
            From the best location for international exposure to the best in lifestyle, find a
            school in the city you love.
          </p>
          <div className={`${!isMobile && styles.destContainer}`}>
            <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-3 pb-3 pt-1`}>
              {countries?.data.map((country: Country) => (
                <DestinationCard destination={country} key={country.id} />
              ))}
            </div>
          </div>
          <div
            className={`${
              !isMobile && styles.discoverTitle
            } d-flex flex-row justify-content-between mt-5`}
          >
            <p
              className={`${
                isMobile ? textStyles['text-primary-18-600'] : textStyles['text-primary-24-600']
              } col-10`}
            >
              Discover popular universites
            </p>
            {isMobile ? (
              <Link className={styles.viewMoreMobile} to="explore" onClick={() => navigateTop()}>
                <u>more</u>
              </Link>
            ) : (
              <Link className={styles.viewMore} to="explore" onClick={() => navigateTop()}>
                <p>View more &nbsp;&nbsp;&rarr;</p>
              </Link>
            )}
          </div>
          <p
            className={`${
              isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-24-400']
            }`}
          >
            Get an opportunity to study at world-class universities and start your educational
            journey on the right foot.
          </p>
          <div className={`${!isMobile && styles.destContainer}`}>
            <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-3 pb-3 pt-1`}>
              {schools?.data.map((school: School) => (
                <UniversityCard school={school} key={school.id} />
              ))}
            </div>
          </div>
          <div
            className={`${
              !isMobile && styles.discoverTitle
            } d-flex flex-row justify-content-between mt-5`}
          >
            <p
              className={`${
                isMobile ? textStyles['text-primary-18-600'] : textStyles['text-primary-24-600']
              } col-10`}
            >
              Explore our blog
            </p>
            {isMobile ? (
              <a
                className={styles.viewMoreMobile}
                href="https://blog.unifyapply.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <u className="col-2">more</u>
              </a>
            ) : (
              <a
                className={styles.viewMore}
                href="https://blog.unifyapply.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <p>View more &nbsp;&nbsp;&rarr;</p>
              </a>
            )}
          </div>
          <p
            className={`${
              isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-24-400']
            }`}
          >
            Get access to our blog for insightful and actionable information to help make your
            journey smoother.
          </p>
          <div className={`${!isMobile && styles.destContainer}`}>
            <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-4 pb-3 pt-1`}>
              {blogEntries?.data.map((blogCard: Blog) => (
                <div className={styles.blogCard} key={blogCard.entry_id.guid}>
                  <div className={styles.blogImageContainer}>
                    <img
                      src={blogCard.image}
                      className={styles.blogImage}
                      alt={`"${blogCard.title}" post image`}
                    />
                  </div>
                  <div className={styles.date}>
                    <Text size={isMobile ? 12 : 18} color="secondary">
                      {new Date(blogCard.published).toLocaleDateString('en-GB', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                  </div>
                  <div className={styles.title}>
                    <Text size={isMobile ? 13 : 24} color="primary" weight={600}>
                      {blogCard.title}
                    </Text>
                  </div>
                  <div className={styles.more}>
                    <Text size={isMobile ? 14 : 18} color="secondary">
                      <a href={blogCard.url} target="_blank" rel="noopener noreferrer">
                        <div>Read More</div>
                        <div>
                          {isMobile ? (
                            <i className="bi bi-arrow-right-short"></i>
                          ) : (
                            <i className="bi bi-chevron-right"></i>
                          )}
                        </div>
                      </a>
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* <div className={styles.colContainer}>
          <div>
            {isMobile ? (
              <p className={`${textStyles['text-primary-25-700']} text-center`}>
                People Can&#39;t Stop
                <br /> Talking about Us
              </p>
            ) : (
              <p className={textStyles['text-primary-36-700']}>
                People Can&#39;t Stop Talking about Us
              </p>
            )}
            <p className={styles.bar}></p>
          </div>
          <div className={styles.beigeContainer}>
            {isMobile ? (
              <img src={BlogReview} className={styles.blogReviewImage} alt="blogReview" />
            ) : (
              <img src={BlogReviewDP} className={styles.blogReviewImage} alt="blogReviewDP" />
            )}
          </div>
          <div className={`${!isMobile && 'd-none'} mt-4`}>
            <i className={`bi bi-chevron-left ${styles.vector}`}></i>
            <i className={`bi bi-chevron-right ${styles.vector}`}></i>
          </div>
        </div> */}
        <div className={styles.colContainer}>
          <p
            className={`${
              isMobile ? textStyles['text-primary-20-600'] : textStyles['text-primary-36-600']
            }`}
          >
            We Help Students Worldwide
          </p>
          <p
            className={`${
              isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-20-400']
            }`}
            style={{ maxWidth: '90%', textAlign: 'center' }}
          >
            We help students from all corners of the globe to study abroad and achieve their dreams.
            We believe everyone deserves a chance to pursue their passions and reach their full
            potential, and we&#39;re proud to help so many students do just that.
          </p>
          <div className="mt-5 mb-5">
            <img src={World} className={styles.worldImage} alt="world" />
          </div>
        </div>
        <div className={styles.partnersContainer}>
          <div className={`${styles.beigeContainer}`}>
            <p
              className={
                isMobile ? textStyles['text-primary-20-600'] : textStyles['text-primary-36-600']
              }
              style={{
                paddingTop: '10px',
                textAlign: 'center',
              }}
            >
              Our 150+ Partners
            </p>
            <div className={`${!isMobile && styles.destContainer}`}>
              <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-4 pb-3 pt-1`}>
                {partners?.data.map((partner: School) => (
                  <div className={`${isMobile ? 'col-5' : 'col-2 ms-5'}`} key={partner.id}>
                    <img
                      src={`${partner.logo}`}
                      className={`${styles.logoContainer}`}
                      alt={`Logo of ${partner.name}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.newsletterConatiner}>
          {isMobile && (
            <div>
              <img src={Newsletter} className={styles.bgNewsletterContainer} alt="newsletter_bg" />
            </div>
          )}
          <div
            className={`${
              isMobile
                ? 'd-flex flex-column align-items-center'
                : 'col-6 d-flex flex-column align-items-start ms-5'
            } pt-4`}
          >
            <p
              className={`${
                isMobile ? textStyles['text-secondary-18-600'] : textStyles['text-secondary-40-600']
              }`}
              style={{ textAlign: 'left' }}
            >
              Subscribe our{' '}
              <span className={!isMobile ? styles.newsletterBg : ''}>newsletter!</span>
            </p>
            <p
              className={`${
                isMobile ? textStyles['text-secondary-14-400'] : textStyles['text-secondary-20-400']
              }`}
              style={{ maxWidth: '70%', textAlign: `${isMobile ? 'center' : 'left'}` }}
            >
              Sign up to receive valuable information, actionable advice and exclusive insights on
              studying abroad.
            </p>
          </div>
          {isMobile ? (
            <div>
              <div className={`mt-4 pb-5 newsletterForm ${styles.customSearch}`}>
                <form>
                  <input className={`${styles.customSearchInput}`} placeholder="Email" />
                  <button className={`btn btn-primary ${styles.customSearchButton}`} type="submit">
                    <p style={{ fontWeight: 700, fontSize: 16 }}>Subscribe</p>
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="col-6 d-flex flex-column align-items-center">
              <div className={`mt-5 pb-5 newsletterForm ${styles.customSearch}`}>
                <form>
                  <input className={`${styles.customSearchInput}`} placeholder="Enter your email" />
                  <button className={`btn btn-primary ${styles.customSearchButton}`} type="submit">
                    <p style={{ fontWeight: 700, fontSize: 16 }}>Subscribe</p>
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Landing;
